import {
  CMD,
  ERR_CODE,
  NO_SERIAL_STRING,
  PROTO_BAUD,
  TIMEOUT_MS,
  TIMEOUT_WRITE_MS,
  decodeText,
  decodeUint,
  describeFrame,
  isSerialString,
  parseBootData,
  parseFault,
  parseFirmwareVersion,
  parseModuleData,
  parseSerialNumber,
} from './eduProtocol';
import EduProtocolClient, { TIMEOUT } from './eduProtocolClient';
import {
  PROFILES,
  describeModule,
  interpretModule,
  readingProfileFor,
} from './moduleTable';
import { getSerialSession } from './serialSession';
import { logEvent, logProtocol, logProtocolError } from './traceabilityLog';

/**
 * Lo que hacemos con una placa vinculada: preguntarle si tiene el firmware de
 * trazabilidad y, si lo tiene, preguntarle cada tanto qué módulos ve.
 *
 * Uno por puerto. No sabe nada de React ni de redux: avisa por listeners y quien
 * quiera lo pinta.
 */

export const STATUS = {
  IDLE: 'IDLE',
  PROBING: 'PROBING',
  // La placa no contestó el ping: asumimos que el firmware no está instalado.
  MISSING: 'MISSING',
  READY: 'READY',
};

/**
 * Cómo salió la lectura del número de producción. Los cuatro casos llevan a
 * cosas distintas, por eso no alcanza con el valor y un null:
 *
 *   PRESENT      la placa ya tiene número; se manda tal cual en el evento.
 *   EMPTY        no tiene ninguno; hay que pedirle uno al servidor de seriales.
 *   UNSUPPORTED  el firmware es anterior al SerialString; se arregla reinstalando.
 *   ERROR        no se pudo leer; se reintenta.
 */
export const SERIAL_STRING = {
  PRESENT: 'PRESENT',
  EMPTY: 'EMPTY',
  UNSUPPORTED: 'UNSUPPORTED',
  ERROR: 'ERROR',
};

/**
 * Cómo salió la grabación. Lo que decide es qué se hace con el número que
 * entregó el servidor: OK se confirma como ocupado y el resto se devuelve, así
 * que el motivo viaja en esa devolución y no se pierde.
 *
 *   OK           grabado y verificado releyendo.
 *   INVALID      lo que llegó no tiene la forma que acepta la placa; no se escribió.
 *   REJECTED     la placa lo rechazó (BADDAT, NRDY, o firmware sin STSS).
 *   UNVERIFIED   contestó que sí, pero la relectura no coincide: no se puede dar
 *                por grabado, y el número tampoco por libre.
 *   ERROR        no contestó.
 */
export const WRITE = {
  OK: 'OK',
  INVALID: 'INVALID',
  REJECTED: 'REJECTED',
  UNVERIFIED: 'UNVERIFIED',
  ERROR: 'ERROR',
};

/**
 * Cuánto se espera desde que se abre el puerto hasta el primer ping.
 *
 * Corto a propósito: quien cubre a la placa que todavía está arrancando es la
 * ventana de reintentos de abajo, no esta espera. Un ping que llega antes de
 * tiempo son bytes en la UART que nadie lee todavía, así que no cuesta nada, y
 * empezar enseguida hace que una placa que sí tiene firmware conteste de una.
 */
const BOOT_SETTLE_MS = 300;

/**
 * Lo mismo, pero después de grabar la placa, que sí está caída un rato.
 *
 * No tiene que cubrir la re-enumeración del USB: cuando eso pasa cambia el objeto
 * SerialPort, y el componente tira este link y arma uno nuevo desde cero. Solo
 * cubre el reinicio.
 */
const REBOOT_SETTLE_MS = 1500;

/**
 * Cuánto se insiste con el ping antes de dar por hecho que no hay firmware.
 *
 * Un solo intento no alcanza: cuánto tarda la placa en arrancar y en devolver el
 * puerto no es fijo. Mientras la ventana esté abierta se vuelve a intentar, así
 * que una placa lenta termina respondiendo en vez de contarse como vacía.
 *
 * Es lo único que se paga cuando la placa no tiene firmware -- es lo que tarda en
 * aparecer el modal -- porque en el caso bueno la primera respuesta corta todo, y
 * cualquier trama que mande la placa sola lo corta antes todavía.
 *
 * Tiene que ser más larga que el timeout de un pedido, que ahora es generoso para
 * aguantar el delay() del código del usuario (ver TIMEOUT_MS): con una ventana más
 * corta el bucle se cerraba antes de que terminara el primer intento.
 */
const PROBE_WINDOW_MS = 12000;

/**
 * Reintentos de cada pedido del probe.
 *
 * Uno solo, en vez de los tres que usa el protocolo por defecto: acá el reintento
 * es el loop de la ventana. Con tres, cada vuelta se comía 1,5 s y la ventana
 * daba para 5 pings; con uno da para 6 en la mitad del tiempo, y corta antes.
 */
const PROBE_RETRIES = 1;

/**
 * Las velocidades a las que se busca a la placa además de la del puerto. La del
 * protocolo es a la que arranca el firmware; 115200 es la que más se usa en el
 * Serial.begin() de los alumnos, y el del usuario corre después del de
 * sistema.begin() y le cambia la UART al firmware también.
 */
const PROBE_BAUD_RATES = [PROTO_BAUD, 115200];

// Entre intento e intento cuando el puerto todavía no se pudo abrir.
const PORT_RETRY_MS = 500;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class TraceabilityLink {
  constructor(port, {
    portName = '',
    baudRate = PROTO_BAUD,
    bootSettle = BOOT_SETTLE_MS,
    rebootSettle = REBOOT_SETTLE_MS,
    probeTimeout = TIMEOUT_MS,
    probeWindow = PROBE_WINDOW_MS,
    /**
     * La ventana de cada velocidad después de la primera: lo que dura un ping.
     * Lo largo -- esperar a que la placa arranque -- ya lo cubrió la primera.
     */
    retryProbeWindow = probeTimeout,
    // Las velocidades a probar antes que las de siempre, en orden.
    preferredBaudRates = () => [],
    writeTimeout = TIMEOUT_WRITE_MS,
    board = {},
  } = {}) {
    this.portName = portName;
    // Con qué columna de la tabla se nombran los módulos de esta placa.
    this.readingProfile = readingProfileFor(board);
    this.bootSettle = bootSettle;
    this.rebootSettle = rebootSettle;
    this.probeTimeout = probeTimeout;
    this.probeWindow = probeWindow;
    this.retryProbeWindow = retryProbeWindow;
    this.preferredBaudRates = preferredBaudRates;
    this.writeTimeout = writeTimeout;
    this.session = getSerialSession(port, { baudRate });
    this.client = new EduProtocolClient(this.session);
    this.status = STATUS.IDLE;
    // El serial de hardware, que llega con la respuesta del ping.
    this.serialNumber = null;
    /**
     * El número de producción de la EEPROM, que es con el que la placa se
     * identifica en trazabilidad. No se lee en el ping: el ping solo averigua si
     * hay firmware, y este lo pide la máquina de identidad cuando le toca.
     */
    this.serialString = null;
    this.firmwareVersion = null;
    /**
     * Si la placa mandó algo por su cuenta. Es la prueba más barata de que el
     * firmware está: los eventos salen sin que nadie los pida y sin depender de que
     * el loop del usuario esté libre -- el de arranque sale desde begin().
     */
    this.sawFrame = false;
    // Puerto lógico del kit (P0..P9) -> lo último que se supo de él.
    this.modules = new Map();
    /**
     * Lo que hace falta para nombrar un módulo además de su propia lectura: la
     * última lectura cruda de cada puerto (la Power compensa la carga de los
     * otros) y el último 5V medido (la Mini normaliza con él).
     */
    this.knownMv = {};
    this.supplyMv = null;
    this.listeners = new Set();
    this.disposed = false;
    this.eventSubscription = null;
    this.owner = `traceability-${portName || 'port'}`;
  }

  subscribe = (listener) => {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot = () => ({
    status: this.status,
    serialNumber: this.serialNumber,
    serialString: this.serialString,
    firmwareVersion: this.firmwareVersion,
    modules: Array.from(this.modules.values()),
  })

  emit = () => {
    const snapshot = this.snapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }

  setStatus = (status) => {
    if (this.status === status) return;
    this.status = status;
    this.emit();
  }

  /**
   * Pide el puerto. Puede fallar porque la placa todavía no volvió del reinicio, o
   * porque el puerto está prestado al flasheo; ninguna de las dos es definitiva y
   * el que llama vuelve a intentar.
   *
   * Lo que se devuelve es si el puerto quedó abierto de verdad, y no si acquire()
   * no tiró. Con la sesión suspendida acquire() anota al dueño y no abre nada, así
   * que el "sí" de antes mandaba a preguntarle a un puerto cerrado: los pings se
   * escribían en el vacío y la placa figuraba como si no tuviera firmware.
   */
  openPort = async () => {
    try {
      await this.session.acquire(this.owner);
      return this.session.isOpen;
    } catch (error) {
      return false;
    }
  }

  /**
   * Abre el puerto, espera a que la placa arranque y le pregunta el serial.
   *
   * El GTSN es solo la excusa: lo que se está averiguando es si hay alguien del
   * otro lado que hable el protocolo.
   *
   * Se insiste hasta que se agota la ventana en vez de preguntar una sola vez,
   * porque cuánto tarda la placa en estar lista no es fijo: después de grabarla
   * se reinicia y, con USB nativo, encima se desconecta y se vuelve a enumerar.
   * Con un solo intento el resultado dependía de si ese momento caía justo bien,
   * y cuando caía mal el modal de "falta instalar" aparecía recién instalado.
   */
  start = async ({ settle = this.bootSettle } = {}) => {
    if (this.disposed || this.status !== STATUS.IDLE) return this.status;
    this.setStatus(STATUS.PROBING);

    this.client.start();
    if (!this.eventSubscription) {
      this.eventSubscription = this.client.events.subscribe(this.handleEvent);
    }

    /**
     * Se la busca en cada velocidad candidata, la primera con la ventana entera y
     * las demás con un ping. Si no aparece en ninguna, el puerto vuelve a la
     * velocidad con la que empezó, que es la del protocolo.
     */
    const initialBaud = this.session.baudRate;
    const bauds = this.probeBaudRates();
    for (let i = 0; i < bauds.length && !this.disposed; i += 1) {
      /* eslint-disable no-await-in-loop */
      if (bauds[i] !== this.session.baudRate) {
        logProtocol(this.portName, `Se busca la placa a ${bauds[i]} baudios.`);
        await this.session.setBaudRate(bauds[i]);
      }
      const found = await this.probe(i === 0 ? settle : this.bootSettle, i === 0 ? this.probeWindow : this.retryProbeWindow);
      if (found) return this.status;
      /* eslint-enable no-await-in-loop */
    }

    if (this.disposed) return this.status;
    await this.session.setBaudRate(initialBaud);
    logProtocol(this.portName, `La placa no respondió al ping a ${bauds.join(', ')} baudios: falta el firmware de trazabilidad.`);
    this.setStatus(STATUS.MISSING);
    return this.status;
  }

  /**
   * Las velocidades a las que se busca a la placa, en orden y sin repetir: primero
   * las que se sabe que usa -- la del último código que se le cargó --, después la
   * del puerto como está, y al final las de siempre.
   */
  probeBaudRates = () => [...new Set([
    ...this.preferredBaudRates(),
    this.session.baudRate,
    ...PROBE_BAUD_RATES,
  ].filter(Boolean))];

  /**
   * Una ventana de pings a la velocidad que tiene el puerto. Dice si encontró al
   * firmware, y en ese caso deja el link en READY.
   */
  probe = async (settle, window) => {
    await delay(settle);
    let deadline = Date.now() + window;

    while (!this.disposed && Date.now() < deadline) {
      /* eslint-disable no-await-in-loop */
      if (!await this.openPort()) {
        const lent = this.session.isSuspended;
        await delay(PORT_RETRY_MS);
        /**
         * Mientras el puerto está prestado -- lo tiene el flasheo -- la ventana no
         * corre. Lo que se está midiendo es si la placa contesta, y no puede
         * contestar por un puerto que es de otro; dejar correr el reloj ahí
         * terminaba pidiendo instalar el firmware que se estaba instalando.
         *
         * Se reinicia al salir de la espera y no al entrar, si no la espera misma
         * se come la ventana y el primer ping de verdad llega sin tiempo.
         */
        if (lent) deadline = Date.now() + window;
      } else {
        try {
          const frame = await this.client.request(CMD.GTSN, undefined, { timeout: this.probeTimeout, retries: PROBE_RETRIES });
          /**
           * Contestó algo, así que el firmware está -- que es lo único que el ping
           * quería saber. Que el serial venga vacío no lo cambia: "00000000" es lo
           * que contesta una placa a la que el firmware no le pudo leer el suyo, y
           * esa placa tiene firmware igual.
           */
          this.serialNumber = parseSerialNumber(decodeText(frame.data));
          logProtocol(this.portName, `Firmware presente a ${this.session.baudRate} baudios. Serial de hardware:`, this.serialNumber || 'sin grabar');
          this.setStatus(STATUS.READY);
          this.readModules();
          return true;
        } catch (error) { /* se reintenta mientras quede ventana */ }
      }
      /* eslint-enable no-await-in-loop */
    }

    if (this.disposed) return false;
    /**
     * Se acabó la ventana sin respuesta. Si en el medio la placa mandó algo por su
     * cuenta, el firmware está y lo que faltó fue turno: se da por buena igual, sin
     * serial de hardware, que es un dato de más y no la identidad.
     *
     * Decir que falta el firmware de una placa que acaba de anunciar que arrancó
     * era mandar a reinstalar lo que ya estaba instalado, una y otra vez.
     */
    if (this.sawFrame) {
      logProtocol(this.portName, 'La placa no contestó el ping pero mandó eventos: el firmware está, el loop debe estar ocupado.');
      this.setStatus(STATUS.READY);
      this.readModules();
      return true;
    }
    return false;
  }

  /**
   * Deja que se vuelva a probar con start() después de un MISSING, sin soltar el
   * puerto. Es para quien tiene motivos para creer que el firmware está.
   */
  rearm = () => {
    if (this.status === STATUS.MISSING) this.status = STATUS.IDLE;
  }

  /**
   * La versión del firmware, para la consola y para el pedido al servidor de
   * seriales. No se decide nada con ella: ver readSerialString.
   */
  readFirmwareVersion = async () => {
    if (this.disposed || !await this.openPort()) return null;
    try {
      const frame = await this.client.request(CMD.GTFW);
      const text = decodeText(frame.data);
      this.firmwareVersion = parseFirmwareVersion(text);
      logProtocol(this.portName, 'Firmware:', this.firmwareVersion || text);
      this.emit();
      return this.firmwareVersion;
    } catch (error) {
      logProtocolError(this.portName, 'No se pudo leer la versión del firmware:', error.code || error.message);
      return null;
    }
  }

  /**
   * El número de producción grabado en la EEPROM, que es con el que la placa se
   * identifica en trazabilidad.
   *
   * Que el firmware soporte el SerialString se resuelve por la respuesta y no por
   * la versión: una tabla de versiones mínimas sería una cosa más para mantener
   * sincronizada entre dos repos, y un BADCMD dice lo mismo sin desactualizarse.
   * Todo firmware que habla el protocolo binario tiene GTSS, así que hoy es un
   * caso que no debería verse; queda por si un firmware futuro lo retira.
   *
   * Un dato con forma rara cuenta como error y no como placa sin grabar: las dos
   * cosas se arreglan distinto, y tomar ruido por una EEPROM vacía haría que se
   * le grabe un número nuevo a una placa que quizá ya tiene el suyo.
   */
  readSerialString = async () => {
    if (this.disposed || !await this.openPort()) return { status: SERIAL_STRING.ERROR };
    try {
      const frame = await this.client.request(CMD.GTSS);
      const text = decodeText(frame.data);
      if (text === NO_SERIAL_STRING) {
        this.serialString = null;
        this.emit();
        logProtocol(this.portName, 'La placa no tiene número de producción grabado.');
        return { status: SERIAL_STRING.EMPTY };
      }
      if (!isSerialString(text)) {
        logProtocolError(this.portName, 'GTSS contestó algo que no es un número de producción:', text);
        return { status: SERIAL_STRING.ERROR };
      }
      this.serialString = text;
      this.emit();
      logProtocol(this.portName, 'Número de producción:', text);
      return { status: SERIAL_STRING.PRESENT, value: text };
    } catch (error) {
      if (error.code === ERR_CODE.BADCMD) {
        logProtocolError(this.portName, 'El firmware de la placa es anterior al número de producción: hay que reinstalarlo.');
        return { status: SERIAL_STRING.UNSUPPORTED };
      }
      logProtocolError(this.portName, 'No se pudo leer el número de producción:', error.code || error.message);
      return { status: SERIAL_STRING.ERROR, code: error.code };
    }
  }

  /**
   * Graba el número que entregó el servidor de seriales.
   *
   * La forma se chequea antes de escribir: la placa rechaza con BADDAT cualquier
   * cosa que no sean 8 caracteres de texto seguro, así que mandarlo sería gastar el
   * viaje para enterarnos de algo que ya sabíamos, y el que llama necesita poder
   * distinguir "el servidor mandó cualquier cosa" de "la placa falló".
   *
   * Y se verifica releyendo, en vez de confiar en el ACK: lo que el ACK confirma
   * es que la placa aceptó el pedido, no que la EEPROM haya quedado escrita, y
   * entre las dos cosas está justamente lo que puede fallar (el fault EEW). Es el
   * mismo chequeo que hace la herramienta de manufactura.
   *
   * El reintento del protocolo no molesta acá: grabar dos veces el mismo valor
   * deja la EEPROM igual, así que un ACK que se perdió se recupera solo.
   */
  writeSerialString = async (value) => {
    if (!isSerialString(value)) {
      logProtocolError(this.portName, 'El número recibido no tiene la forma que acepta la placa:', value);
      return { status: WRITE.INVALID };
    }
    if (this.disposed || !await this.openPort()) return { status: WRITE.ERROR };

    try {
      const ack = await this.client.request(CMD.STSS, value, { timeout: this.writeTimeout });
      // El ACK repite el dato que la placa dio por bueno. Uno distinto del que se
      // mandó no es un éxito, aunque haya venido como ACK.
      const echoed = decodeText(ack.data);
      if (echoed !== value) {
        logProtocolError(this.portName, 'La placa confirmó un número distinto del que se le mandó:', echoed);
        return { status: WRITE.UNVERIFIED };
      }
    } catch (error) {
      const rejected = error.code && error.code !== TIMEOUT;
      logProtocolError(this.portName, 'No se pudo grabar el número de producción:', error.code || error.message);
      return { status: rejected ? WRITE.REJECTED : WRITE.ERROR, code: error.code };
    }

    const reread = await this.readSerialString();
    if (reread.status !== SERIAL_STRING.PRESENT || reread.value !== value) {
      logProtocolError(this.portName, 'El número grabado no coincide con el que se escribió.');
      return { status: WRITE.UNVERIFIED };
    }
    logProtocol(this.portName, 'Número de producción grabado y verificado:', value);
    return { status: WRITE.OK, value };
  }

  /**
   * Vuelve al estado inicial para volver a probar, que es lo que hace falta
   * después de instalar el firmware. El puerto se suelta: el flasheo lo necesita
   * para él solo, y el próximo start() lo abre de nuevo.
   */
  reset = async () => {
    this.client.stop();
    this.modules.clear();
    this.knownMv = {};
    this.supplyMv = null;
    this.serialNumber = null;
    this.serialString = null;
    this.firmwareVersion = null;
    this.sawFrame = false;
    this.status = STATUS.IDLE;
    this.emit();
    await this.session.release(this.owner);
  }

  /**
   * El 5V con el que la Mini normaliza sus lecturas (GTVL). Solo se pide en las
   * placas cuya tabla lo usa: en las demás contesta NRDY, y preguntar es gastar un
   * viaje. Si no se puede leer, la tabla asume el nominal.
   */
  readSupply = async () => {
    if (!PROFILES[this.readingProfile.key].supplyValidMv) return;
    try {
      const frame = await this.client.request(CMD.GTVL);
      this.supplyMv = decodeUint(frame.data);
    } catch (error) {
      this.supplyMv = null;
      logProtocolError(this.portName, 'No se pudo leer el 5V de los módulos; se asume el nominal:', error.code || error.message);
    }
  }

  // Nombra la lectura de un puerto con la tabla de esta placa y la guarda.
  storeModule = ({ port, mv }) => {
    const reading = interpretModule(this.readingProfile, port, mv, { knownMv: this.knownMv, supplyMv: this.supplyMv });
    this.modules.set(port, reading);
    return reading;
  }

  /**
   * Los módulos que la placa ve, una sola vez.
   *
   * Acá había un barrido cada dos segundos. Ya no hace falta: la placa avisa sola
   * con un EVMD en las dos transiciones, al enchufar y al desenchufar, así que lo
   * que el barrido traía ya viene por su cuenta y antes. Repetirlo era mandar un
   * bloque de diez tramas por minuto y medio a cada placa para enterarse de nada.
   *
   * Lo que el evento no puede dar es el estado del momento en que nos
   * enganchamos: las transiciones de una placa que ya venía andando ocurrieron
   * antes de que abriéramos el puerto. De ahí que quede este único barrido al
   * conectar -- la foto inicial -- y de ahí en más manden los eventos.
   *
   * Todas las lecturas se guardan antes de nombrar ninguna, porque la Power
   * compensa cada puerto con lo que hay enchufado en los demás.
   */
  readModules = async () => {
    await this.readSupply();
    // Se pudo haber ido mientras se esperaba el 5V: un pedido más ya no lo
    // escucha nadie y deja su timer andando.
    if (this.disposed || this.status !== STATUS.READY) return;
    try {
      const frames = await this.client.requestBlock(CMD.GTMA);
      const values = frames
        .filter((frame) => frame.cmd === CMD.GTMD)
        .map((frame) => parseModuleData(frame.data))
        .filter(Boolean);
      values.forEach(({ port, mv }) => { this.knownMv[port] = mv; });
      const readings = values.map(this.storeModule);
      const connected = readings.filter((reading) => reading.present);
      logProtocol(this.portName, `Módulos (${connected.length}):`, connected.length > 0
        ? connected.map(describeModule).join(', ')
        : 'ninguno');
      this.emit();
    } catch (error) {
      logProtocolError(this.portName, 'No se pudieron leer los módulos:', error.code || error.message);
    }
  }

  /**
   * Los eventos que la placa manda sola. Se loguean todos, incluso los que no
   * sabemos interpretar: es información que la placa consideró que valía la pena
   * mandar, y perderla en silencio no ayuda a nadie que esté mirando la consola.
   */
  handleEvent = (frame) => {
    // La placa habló: haya contestado o no nuestros pings, el firmware está.
    this.sawFrame = true;
    switch (frame.cmd) {
      /**
       * El EVMD sale en las dos transiciones: una lectura en 0 es el módulo que se
       * fue.
       *
       * Se nombra solo el puerto del evento, con lo último que se sabe de los
       * demás. Enchufar un módulo también corre un poco las lecturas de los otros
       * puertos de la Power, pero eso ya lo corrige la compensación de carga.
       */
      case CMD.EVMD: {
        const value = parseModuleData(frame.data);
        if (value) {
          this.knownMv[value.port] = value.mv;
          const reading = this.storeModule(value);
          logEvent(this.portName, reading.present
            ? `Módulo nuevo: ${describeModule(reading)}`
            : `Módulo desconectado de P${reading.port}`);
          this.emit();
        }
        break;
      }
      case CMD.EVBT: {
        const boot = parseBootData(frame.data);
        logEvent(this.portName, `La placa arrancó (${boot.kind}), uptime previo ${boot.previousUptime}s`);
        break;
      }
      case CMD.EVFL:
        logEvent(this.portName, `Fault en la placa: ${parseFault(frame.data) || 'desconocido'}`);
        break;
      case CMD.EVUP:
        logEvent(this.portName, `Uptime: ${decodeUint(frame.data)}s`);
        break;
      default:
        logEvent(this.portName, describeFrame(frame));
        break;
    }
  }

  dispose = async () => {
    if (this.disposed) return;
    this.disposed = true;
    if (this.eventSubscription) this.eventSubscription.unsubscribe();
    this.eventSubscription = null;
    this.client.stop();
    this.listeners.clear();
    await this.session.release(this.owner);
  }
}

export default TraceabilityLink;
